// Print Report Utility for generating reports based on different data types

/**
 * Generates a printable report for different entity types
 * @param {string} reportType - Type of report (trailers, trucks, yards, buildings, users)
 * @param {Array} data - The data to include in the report
 * @param {Object} options - Additional options for the report
 */
export const generateReport = (reportType, data, options = {}) => {
  if (!data || data.length === 0) {
    alert('No data available to generate report');
    return;
  }

  // Create a new window for the report
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Please allow pop-ups to generate reports');
    return;
  }

  // Get the current date/time for the report header
  const now = new Date();
  const dateString = now.toLocaleDateString();
  const timeString = now.toLocaleTimeString();

  // Start with a basic HTML structure for the report
  let reportContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${options.title || `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          line-height: 1.4;
          position: relative;
        }
        .print-button {
          position: absolute;
          top: 20px;
          right: 20px;
          z-index: 100;
        }
        .print-button button {
          padding: 8px 16px;
          font-size: 16px;
          cursor: pointer;
          background-color: #4a5568;
          color: white;
          border: none;
          border-radius: 4px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .print-button button:hover {
          background-color: #2d3748;
        }
        .report-header {
          text-align: center;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #333;
          padding-top: 15px;
        }
        .report-title {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 5px;
        }
        .report-date {
          font-size: 14px;
          color: #666;
        }
        .report-subtitle {
          font-size: 18px;
          margin: 15px 0;
          padding-bottom: 5px;
          border-bottom: 1px solid #ccc;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .footer {
          margin-top: 30px;
          font-size: 12px;
          text-align: center;
          color: #666;
        }
        @media print {
          body {
            margin: 0;
            padding: 15px;
          }
          .print-button {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-button">
        <button onclick="window.print()">Print Report</button>
      </div>
      <div class="report-header">
        <div class="report-title">${options.title || `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`}</div>
        <div class="report-date">Generated on ${dateString} at ${timeString}</div>
        ${options.subtitle ? `<div class="report-subtitle">${options.subtitle}</div>` : ''}
      </div>
  `;

  // Generate different report content based on report type
  switch (reportType) {
    case 'trailers':
      reportContent += generateTrailersReport(data);
      break;
    case 'trucks':
      reportContent += generateTrucksReport(data);
      break;
    case 'yards':
      reportContent += generateYardsReport(data);
      break;
    case 'buildings':
      reportContent += generateBuildingsReport(data);
      break;
    case 'users':
      reportContent += generateUsersReport(data);
      break;
    default:
      reportContent += '<p>Unsupported report type</p>';
  }

  // Add footer and close HTML
  reportContent += `
      <div class="footer">
        <p>Trucking Yard Management System &copy; ${new Date().getFullYear()}</p>
      </div>
    </body>
    </html>
  `;

  // Write the content to the new window and trigger print
  printWindow.document.open();
  printWindow.document.write(reportContent);
  printWindow.document.close();
};

/**
 * Exports data to Excel file format and triggers download
 * @param {string} reportType - Type of report (trailers, trucks, yards, buildings, users)
 * @param {Array} data - The data to include in the report
 * @param {Object} options - Additional options for the report
 */
export const exportToExcel = (reportType, data, options = {}) => {
  if (!data || data.length === 0) {
    alert('No data available to export');
    return;
  }

  // Get headers and rows based on report type
  let headers = [];
  let rows = [];

  switch (reportType) {
    case 'trailers':
      headers = [
        'Trailer Number', 'Location', 'Type', 'License Plate',
        'Status', 'Status Description', 'Owner', 'Load Number',
        'Created', 'Updated'
      ];

      rows = data.map(trailer => {
        let status = "Available";
        if (trailer.red_tagged) status = "Red Tagged";
        else if (trailer.yellow_tagged) status = "Yellow Tagged";
        else if (trailer.in_shop) status = "Shop";

        const createdDate = trailer.created_at ? new Date(trailer.created_at).toLocaleString() : 'N/A';
        const updatedDate = trailer.updated_at ? new Date(trailer.updated_at).toLocaleString() : 'N/A';
        const loadStatus = trailer.is_tray ? trailer.tray_count : trailer.is_loaded ? trailer.load_number : 'Empty';

        return [
          trailer.trailer_number || 'N/A',
          trailer.trailer_location || 'Unknown',
          trailer.type || 'N/A',
          (trailer.license_state || '') + ' - ' + (trailer.license_plate || ''),
          status,
          trailer.tagged_description || 'N/A',
          trailer.company_owner || 'Unknown',
          loadStatus,
          createdDate,
          updatedDate
        ];
      });
      break;

    case 'trucks':
      headers = [
        'Truck Number', 'Location', 'Type', 'Make', 'Model',
        'License Plate', 'Status', 'Status Description', 'Assigned To',
        'Created', 'Updated'
      ];

      rows = data.map(truck => {
        let status = "Available";
        if (truck.red_tagged) status = "Red Tagged";
        else if (truck.yellow_tagged) status = "Yellow Tagged";
        else if (truck.in_shop) status = "Shop";

        const createdDate = truck.created_at ? new Date(truck.created_at).toLocaleString() : 'N/A';
        const updatedDate = truck.updated_at ? new Date(truck.updated_at).toLocaleString() : 'N/A';
        const assignedTo = truck.last_user || (truck.assigned_user_id ? `User ID: ${truck.assigned_user_id}` : 'N/A');

        return [
          truck.truck_number || 'N/A',
          truck.truck_location || 'Unknown',
          truck.type || 'N/A',
          truck.make || 'N/A',
          truck.model || 'N/A',
          truck.license_plate || 'N/A',
          status,
          truck.tagged_description || 'N/A',
          assignedTo,
          createdDate,
          updatedDate
        ];
      });
      break;

    case 'yards':
      headers = [
        'Name', 'Parking Location', 'Has Parking Spots',
        'Created', 'Updated'
      ];

      rows = data.map(yard => {
        const createdDate = yard.created_at ? new Date(yard.created_at).toLocaleString() : 'N/A';
        const updatedDate = yard.updated_at ? new Date(yard.updated_at).toLocaleString() : 'N/A';

        return [
          yard.name || 'N/A',
          yard.parking_location || 'N/A',
          yard.parking_spots ? 'Yes' : 'No',
          createdDate,
          updatedDate
        ];
      });
      break;

    case 'buildings':
      headers = [
        'Building Name', 'Door Number', 'Created', 'Updated'
      ];

      rows = data.map(building => {
        const createdDate = building.created_at ? new Date(building.created_at).toLocaleString() : 'N/A';
        const updatedDate = building.updated_at ? new Date(building.updated_at).toLocaleString() : 'N/A';

        return [
          building.building_name || 'N/A',
          building.building_door_number || 'N/A',
          createdDate,
          updatedDate
        ];
      });
      break;

    case 'users':
      headers = [
        'Alpha Code', 'Email', 'Role', 'Status', 'Created', 'Updated'
      ];

      rows = data.map(user => {
        // Determine user role based on boolean flags
        let role = 'User';
        if (user.is_admin) role = 'Admin';
        else if (user.is_super_manager) role = 'Super Manager';
        else if (user.is_account_manager) role = 'Account Manager';
        else if (user.is_manager) role = 'Manager';
        else if (user.is_yard_driver) role = 'Yard Driver';
        else if (user.is_road_driver) role = 'Road Driver';
        else if (user.is_mechanic) role = 'Mechanic';
        else if (user.is_security) role = 'Security';

        const createdDate = user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A';
        const updatedDate = user.updated_at ? new Date(user.updated_at).toLocaleString() : 'N/A';

        return [
          user.alpha_code || 'N/A',
          user.email || 'N/A',
          role,
          user.is_active ? 'Active' : 'Inactive',
          createdDate,
          updatedDate
        ];
      });
      break;

    default:
      alert('Unsupported report type for Excel export');
      return;
  }

  // Create CSV content
  let csvContent = headers.join(',') + '\n';

  rows.forEach(row => {
    // Escape values that contain commas by wrapping in quotes
    const formattedRow = row.map(cell => {
      // Convert to string and check if it needs to be quoted
      const cellStr = String(cell);
      return cellStr.includes(',') ? `"${cellStr}"` : cellStr;
    });

    csvContent += formattedRow.join(',') + '\n';
  });

  // Get filename
  const accountName = options.subtitle ? options.subtitle.replace('Account: ', '') : '';
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  const filename = `${accountName ? accountName + ' - ' : ''}${reportType}_report_${timestamp}.csv`;

  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Helper functions for generating specific report types
function generateTrailersReport(trailers) {
  let content = `
    <div class="report-subtitle">Trailers Summary</div>
    <p>Total Trailers: ${trailers.length}</p>
    <table>
      <thead>
        <tr>
          <th>Trailer Number</th>
          <th>Location</th>
          <th>Type</th>
          <th>License Plate</th>
          <th>Status</th>
          <th>Status Description</th>
          <th>Owner</th>
          <th>Load Number</th>
          <th>Created</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
  `;

  trailers.forEach(trailer => {
    let status = "Available";
    if (trailer.red_tagged) status = "Red Tagged";
    else if (trailer.yellow_tagged) status = "Yellow Tagged";
    else if (trailer.in_shop) status = "Shop";

    const createdDate = trailer.created_at ? new Date(trailer.created_at).toLocaleString() : 'N/A';
    const updatedDate = trailer.updated_at ? new Date(trailer.updated_at).toLocaleString() : 'N/A';

    content += `
      <tr>
        <td>${trailer.trailer_number || 'N/A'}</td>
        <td>${trailer.trailer_location || 'Unknown'}</td>
        <td>${trailer.type || 'N/A'}</td>
        <td>${(trailer.license_state || '') + ' - ' + (trailer.license_plate || '')}</td>
        <td>${status}</td>
        <td>${trailer.tagged_description || 'N/A'}</td>
        <td>${trailer.company_owner || 'Unknown'}</td>
        <td>${trailer.is_tray ? trailer.tray_count : trailer.is_loaded ? trailer.load_number : 'Empty'}</td>
        <td>${createdDate}</td>
        <td>${updatedDate}</td>
      </tr>
    `;
  });

  content += `
      </tbody>
    </table>
  `;
  return content;
}

function generateTrucksReport(trucks) {
  let content = `
    <div class="report-subtitle">Trucks Summary</div>
    <p>Total Trucks: ${trucks.length}</p>
    <table>
      <thead>
        <tr>
          <th>Truck Number</th>
          <th>Location</th>
          <th>Type</th>
          <th>Make</th>
          <th>Model</th>
          <th>License Plate</th>
          <th>Status</th>
          <th>Status Description</th>
          <th>Assigned To</th>
          <th>Created</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
  `;

  trucks.forEach(truck => {
    let status = "Available";
    if (truck.red_tagged) status = "Red Tagged";
    else if (truck.yellow_tagged) status = "Yellow Tagged";
    else if (truck.in_shop) status = "Shop";

    const createdDate = truck.created_at ? new Date(truck.created_at).toLocaleString() : 'N/A';
    const updatedDate = truck.updated_at ? new Date(truck.updated_at).toLocaleString() : 'N/A';

    // Assigned to user (could be last_user or user ID)
    const assignedTo = truck.last_user || (truck.assigned_user_id ? `User ID: ${truck.assigned_user_id}` : 'N/A');

    content += `
      <tr>
        <td>${truck.truck_number || 'N/A'}</td>
        <td>${truck.truck_location || 'Unknown'}</td>
        <td>${truck.type || 'N/A'}</td>
        <td>${truck.make || 'N/A'}</td>
        <td>${truck.model || 'N/A'}</td>
        <td>${truck.license_plate || 'N/A'}</td>
        <td>${status}</td>
        <td>${truck.tagged_description || 'N/A'}</td>
        <td>${assignedTo}</td>
        <td>${createdDate}</td>
        <td>${updatedDate}</td>
      </tr>
    `;
  });

  content += `
      </tbody>
    </table>
  `;
  return content;
}

function generateYardsReport(yards) {
  let content = `
    <div class="report-subtitle">Yards Summary</div>
    <p>Total Yards: ${yards.length}</p>
    <table>
      <thead>
        <tr>
          <th>Name</th>
          <th>Parking Location</th>
          <th>Has Parking Spots</th>
          <th>Created</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
  `;

  yards.forEach(yard => {
    const createdDate = yard.created_at ? new Date(yard.created_at).toLocaleString() : 'N/A';
    const updatedDate = yard.updated_at ? new Date(yard.updated_at).toLocaleString() : 'N/A';

    content += `
      <tr>
        <td>${yard.name || 'N/A'}</td>
        <td>${yard.parking_location || 'N/A'}</td>
        <td>${yard.parking_spots ? 'Yes' : 'No'}</td>
        <td>${createdDate}</td>
        <td>${updatedDate}</td>
      </tr>
    `;
  });

  content += `
      </tbody>
    </table>
  `;
  return content;
}

function generateBuildingsReport(buildings) {
  let content = `
    <div class="report-subtitle">Buildings Summary</div>
    <p>Total Buildings: ${buildings.length}</p>
    <table>
      <thead>
        <tr>
          <th>Building Name</th>
          <th>Door Number</th>
          <th>Created</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
  `;

  buildings.forEach(building => {
    const createdDate = building.created_at ? new Date(building.created_at).toLocaleString() : 'N/A';
    const updatedDate = building.updated_at ? new Date(building.updated_at).toLocaleString() : 'N/A';

    content += `
      <tr>
        <td>${building.building_name || 'N/A'}</td>
        <td>${building.building_door_number || 'N/A'}</td>
        <td>${createdDate}</td>
        <td>${updatedDate}</td>
      </tr>
    `;
  });

  content += `
      </tbody>
    </table>
  `;
  return content;
}

function generateUsersReport(users) {
  let content = `
    <div class="report-subtitle">Users Summary</div>
    <p>Total Users: ${users.length}</p>
    <table>
      <thead>
        <tr>
          <th>Alpha Code</th>
          <th>Email</th>
          <th>Role</th>
          <th>Status</th>
          <th>Created</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
  `;

  users.forEach(user => {
    // Determine user role based on boolean flags
    let role = 'User';
    if (user.is_admin) role = 'Admin';
    else if (user.is_super_manager) role = 'Super Manager';
    else if (user.is_account_manager) role = 'Account Manager';
    else if (user.is_manager) role = 'Manager';
    else if (user.is_yard_driver) role = 'Yard Driver';
    else if (user.is_road_driver) role = 'Road Driver';
    else if (user.is_mechanic) role = 'Mechanic';
    else if (user.is_security) role = 'Security';

    const createdDate = user.created_at ? new Date(user.created_at).toLocaleString() : 'N/A';
    const updatedDate = user.updated_at ? new Date(user.updated_at).toLocaleString() : 'N/A';

    content += `
      <tr>
        <td>${user.alpha_code || 'N/A'}</td>
        <td>${user.email || 'N/A'}</td>
        <td>${role}</td>
        <td>${user.is_active ? 'Active' : 'Inactive'}</td>
        <td>${createdDate}</td>
        <td>${updatedDate}</td>
      </tr>
    `;
  });

  content += `
      </tbody>
    </table>
  `;
  return content;
}
